// Scenario bar: the slim strip under the document tabs for switching between a case's
// scenarios (versions). Client (folder) on the left; a "Scenarios · <current>" dropdown
// that switches, renames, deletes, adds and duplicates. Wired to the store/Phase-1 API.
import { useEffect, useRef, useState } from 'react';
import { useActiveDoc, useDis } from './store';

const FolderIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="scn-ic"><path d="M3 7h6l2 2h10v10H3z" /></svg>
);
const PencilIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4L18.5 9.5a2 2 0 0 0-2.8-2.8L5 17.2z" /></svg>
);
const ScnIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="scn-ic"><rect x="4" y="5" width="7" height="14" rx="1.5" /><rect x="13" y="5" width="7" height="14" rx="1.5" /></svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 7h14M9 7V5h6v2M7 7l1 13h8l1-13" /></svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
);
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5h10" /></svg>
);
const GripIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="6" r="1.4" /><circle cx="9" cy="12" r="1.4" /><circle cx="9" cy="18" r="1.4" /><circle cx="15" cy="6" r="1.4" /><circle cx="15" cy="12" r="1.4" /><circle cx="15" cy="18" r="1.4" /></svg>
);

export function ScenarioBar() {
  const doc = useActiveDoc();
  const clientId = doc?.clientId ?? null;
  const client = useDis((s) => (clientId ? s.clientCache[clientId] : undefined));
  const loadClient = useDis((s) => s.loadClient);
  const switchScenario = useDis((s) => s.switchScenario);
  const reorderScenarios = useDis((s) => s.reorderScenarios);
  const newScenario = useDis((s) => s.newScenario);
  const duplicateScenario = useDis((s) => s.duplicateScenario);
  const renameScenario = useDis((s) => s.renameScenario);
  const deleteScenario = useDis((s) => s.deleteScenario);
  const renameClient = useDis((s) => s.renameClient);

  const [open, setOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [editingClient, setEditingClient] = useState(false);
  const [clientDraft, setClientDraft] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (clientId) void loadClient(clientId); }, [clientId, loadClient]);
  // Close the menu on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) closeMenu(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMenu(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  if (!doc) return null;

  const scenarios = client?.scenarios ?? [];
  const currentId = doc.caseId;
  const currentLabel = doc.label || scenarios.find((s) => s.id === currentId)?.label || doc.name || 'Scenario';
  const clientName = client?.name || doc.name || 'Untitled';
  const caseNumber = client?.caseNumber || '';
  const saved = !!clientId;

  function closeMenu() { setOpen(false); setRenamingId(null); setConfirmId(null); }

  function switchTo(id: string) { if (id !== currentId) void switchScenario(id); closeMenu(); }
  function onDropRow(targetId: string) {
    if (!dragId || dragId === targetId || !clientId) { setDragId(null); setOverId(null); return; }
    const ids = scenarios.map((s) => s.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    setDragId(null); setOverId(null);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    void reorderScenarios(clientId, ids);
  }
  function startRename(id: string, label: string) { setConfirmId(null); setRenamingId(id); setDraft(label); }
  function commitRename(id: string) {
    const v = draft.trim();
    if (v) void renameScenario(id, v, clientId ?? undefined);
    setRenamingId(null);
  }
  function commitClientRename() {
    const v = clientDraft.trim();
    if (v && clientId) void renameClient(clientId, v);
    setEditingClient(false);
  }

  return (
    <div className="scnbar" ref={wrapRef}>
      <span className="scn-client" title={caseNumber ? `${clientName} · ${caseNumber}` : clientName}>
        <FolderIcon />
        {editingClient ? (
          <input
            className="scn-rename" autoFocus value={clientDraft}
            onChange={(e) => setClientDraft(e.target.value)}
            onBlur={commitClientRename}
            onKeyDown={(e) => { if (e.key === 'Enter') commitClientRename(); if (e.key === 'Escape') setEditingClient(false); }}
          />
        ) : (
          <>
            <span className="scn-cname">{clientName}</span>
            {caseNumber && <span className="scn-cnum">{caseNumber}</span>}
          </>
        )}
      </span>
      {saved && !editingClient && (
        <button type="button" className="scn-pencil" title="Rename client" aria-label="Rename client"
          onClick={() => { setClientDraft(clientName); setEditingClient(true); }}>
          <PencilIcon />
        </button>
      )}

      <span className="scn-divider" aria-hidden="true" />

      <div className="scn-pick-wrap">
        <button type="button" className="scn-pick" disabled={!saved}
          title={saved ? 'Switch, add or manage scenarios' : 'Scenarios are available once the case is saved'}
          aria-haspopup="menu" aria-expanded={open}
          onClick={() => setOpen((v) => !v)}>
          <ScnIcon />
          <span className="scn-pick-ttl">Scenarios</span>
          <span className="scn-pick-cur">· {currentLabel}</span>
          {scenarios.length > 0 && <span className="scn-count">{scenarios.length}</span>}
          <span className="scn-caret" aria-hidden="true">▾</span>
        </button>

        {open && (
          <div className="scn-menu" role="menu">
            <div className="scn-menu-head">{clientName} · scenarios</div>
            {scenarios.map((s) => {
              const canDrag = scenarios.length > 1 && renamingId !== s.id && confirmId !== s.id;
              return (
              <div key={s.id}
                className={`scn-row${s.id === currentId ? ' on' : ''}${dragId === s.id ? ' dragging' : ''}${overId === s.id && dragId && dragId !== s.id ? ' drag-over' : ''}`}
                draggable={canDrag}
                onDragStart={canDrag ? (e) => { setDragId(s.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', s.id); } : undefined}
                onDragOver={(e) => { if (dragId) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overId !== s.id) setOverId(s.id); } }}
                onDrop={(e) => { if (dragId) { e.preventDefault(); onDropRow(s.id); } }}
                onDragEnd={() => { setDragId(null); setOverId(null); }}>
                {renamingId === s.id ? (
                  <input
                    className="scn-rename row" autoFocus value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => commitRename(s.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitRename(s.id); if (e.key === 'Escape') setRenamingId(null); }}
                  />
                ) : confirmId === s.id ? (
                  <div className="scn-confirm">
                    <span>Delete{scenarios.length === 1 ? ' this client?' : '?'}</span>
                    <button type="button" className="scn-cbtn" onClick={() => setConfirmId(null)}>Cancel</button>
                    <button type="button" className="scn-cbtn del" onClick={() => { void deleteScenario(s.id, clientId ?? undefined); closeMenu(); }}>Delete</button>
                  </div>
                ) : (
                  <>
                    <span className={`scn-grip${canDrag ? '' : ' off'}`} aria-hidden="true" title={canDrag ? 'Drag to reorder' : undefined}><GripIcon /></span>
                    <div className="scn-row-main" role="menuitemradio" aria-checked={s.id === currentId} tabIndex={0}
                      onClick={() => switchTo(s.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); switchTo(s.id); } }}>
                      <span className="scn-chk">{s.id === currentId ? '✓' : ''}</span>
                      <span className="scn-lab">{s.label || 'Untitled scenario'}</span>
                    </div>
                    <span className="scn-acts">
                      <button type="button" title="Rename" aria-label="Rename scenario" onClick={() => startRename(s.id, s.label || '')}><PencilIcon /></button>
                      <button type="button" className="del" title="Delete" aria-label="Delete scenario" onClick={() => { setRenamingId(null); setConfirmId(s.id); }}><TrashIcon /></button>
                    </span>
                  </>
                )}
              </div>
              );
            })}
            <hr />
            <button type="button" className="scn-item" onClick={() => { void newScenario({ clientId: clientId ?? undefined, year: doc.data.taxYear }); closeMenu(); }}>
              <PlusIcon />New scenario
            </button>
            <button type="button" className="scn-item" onClick={() => { void duplicateScenario(doc.key); closeMenu(); }}>
              <CopyIcon />Duplicate current scenario
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
